import { clientUserText } from '../../../../terminology/store';
import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useMemo, useRef, useState, useEffect } from "react";
import { ResponsivePie } from "@nivo/pie";

const PIE_COLORS = [
  "#FFB84C",
  "#F16767",
  "#865DFF",
  "#3795BD",
  "#36BA98",
  "#FF9AA2",
  "#9ED2F6",
  "#C7CEEA",
  "#FFDAC1",
  "#B5EAD7",
];

const ReportPieChart = ({
  reportType = "user",
  rawData,
  isLoading = false,
  defaultViewMode = "pie-chart",
}) => {
  useTerminology();
  const chartContainerRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedResellerId, setSelectedResellerId] = useState("");

  // Theme-like colors
  const fixedTextPrimary = "#001F54";
  const fixedTextSecondary = "#546e7a";
  const tooltipBg = "#ffffff";

// -------------------------------
// Dropdown: ALL resellers that appear in user pie (even with 0)
// -------------------------------
const resellerOptions = useMemo(() => {
  const map = new Map();

  // Treat `rawData` as an array of sections (same way user report does)
  const sections = Array.isArray(rawData) ? rawData : [];

  sections.forEach((section) => {
    const id =
      section?.reseller?.id ||
      section?.resellerId ||
      null;

    const name =
      section?.reseller?.reseller_name ||
      section?.resellerUsername ||
      "Unknown";

    if (id != null) {
      map.set(String(id), name);
    }
  });

  return Array.from(map.entries()).map(([id, name]) => ({
    id,
    name,
  }));
}, [rawData]);

  const safeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const groupCount = (items = [], getKey) => {
    const map = new Map();

    items.forEach((item) => {
      const rawKey = getKey(item);
      const key = rawKey == null || rawKey === "" ? "Unknown" : String(rawKey);
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries()).map(([label, value], idx) => ({
      id: label,
      label,
      value,
      color: PIE_COLORS[idx % PIE_COLORS.length],
    }));
  };

  const groupSum = (items = [], getKey, getValue) => {
    const map = new Map();

    items.forEach((item) => {
      const rawKey = getKey(item);
      const key = rawKey == null || rawKey === "" ? "Unknown" : String(rawKey);
      const value = safeNumber(getValue(item));
      map.set(key, (map.get(key) || 0) + value);
    });

    return Array.from(map.entries())
      .filter(([, value]) => value > 0)
      .map(([label, value], idx) => ({
        id: label,
        label,
        value,
        color: PIE_COLORS[idx % PIE_COLORS.length],
      }));
  };

  // -------------------------------
  // USER PIE LOGIC (show zero slices in pie)
  // -------------------------------
  const getUserPieData = (visibleData = [], selectedResellerId) => {
    const rows = [];

    if (!selectedResellerId) {
      // Show every reseller as a slice, even if clientAdmins.length === 0
      visibleData.forEach((section) => {
        const resellerName =
          section?.reseller?.reseller_name ||
          section?.resellerUsername ||
          "Unknown";

        const clientAdmins =
          section?.clientAdmins ||
          section?.client_admins ||
          [];

        rows.push({
          id: resellerName,
          label: resellerName,
          // Keep zero, but keep it visible in pie
          value: clientAdmins.length,
          type: "reseller",
          totalClientAdmins: clientAdmins.length,
        });
      });
    } else {
      const selectedSection = visibleData.find(
        (s) =>
          String(s?.reseller?.id || s?.resellerId) ===
          String(selectedResellerId)
      );

      if (selectedSection) {
        const clientAdmins =
          selectedSection?.clientAdmins ||
          selectedSection?.client_admins ||
          [];

        clientAdmins.forEach((ca) => {
          const name =
            ca?.client_name ||
            ca?.firm_name ||
            ca?.name ||
            "Unknown";

          const totalUsers =
            (ca?.users && ca.users.length) ||
            ca?.userCount ||
            0;

          const totalCases =
            (ca?.cases && ca.cases.length) ||
            ca?.caseCount ||
            0;

          rows.push({
            id: name,
            label: name,
            // Keep zero, still show slice
            value: totalUsers,
            type: "clientAdmin",
            totalUsers,
            totalCases,
          });
        });
      }
    }

    // Do NOT filter out zeros here; let ResponsivePie see value = 0
    return rows.map((item, idx) => ({
      ...item,
      color: PIE_COLORS[idx % PIE_COLORS.length],
    }));
  };

  // -------------------------------
  // USAGE PIE
  // -------------------------------
  const getUsagePieData = (usageCards = []) => {
    const rows = [];
    usageCards.forEach((card) => {
      (card?.rows || []).forEach((row) => rows.push(row));
    });
    return groupCount(rows, (row) => row.sessionTypeLabel || "Unknown");
  };

  // -------------------------------
  // COMMISSION PIE (show ALL resellers in dropdown, even with zero)
  // -------------------------------
  const getCommissionPieData = (rows = [], selectedResellerId) => {
    const result = [];

    if (!selectedResellerId) {
      const map = new Map();
      const allResellers = new Set();

      rows.forEach((row) => {
        if (row.resellerUsername) {
          allResellers.add(row.resellerUsername);
        }
      });

      allResellers.forEach((resellerName) => {
        const filtered = rows.filter(
          (row) => row.resellerUsername === resellerName
        );
        const count = filtered.length;
        const firms = new Set(
          filtered.map((row) => row.firmName).filter(Boolean)
        );

        map.set(resellerName, { count, firms });
      });

      Array.from(map.entries()).forEach(([key, value], idx) => {
        result.push({
          id: key,
          label: key,
          value: value.count,
          type: "reseller",
          totalFirms: value.firms.size,
          color: PIE_COLORS[idx % PIE_COLORS.length],
        });
      });
    } else {
      const selectedResellerIds = selectedResellerId
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const filtered = rows.filter((r) =>
        selectedResellerIds.some((id) => String(r.resellerId) === String(id))
      );

      const map = new Map();
      filtered.forEach((row) => {
        const key = row.firmName || "Unknown";
        map.set(key, (map.get(key) || 0) + 1);
      });

      Array.from(map.entries()).forEach(([key, value], idx) => {
        result.push({
          id: key,
          label: key,
          value,
          type: "firm",
          totalCases: value,
          color: PIE_COLORS[idx % PIE_COLORS.length],
        });
      });
    }

    return result;
  };

  // -------------------------------
  // FINANCIAL
  // -------------------------------
  const getFinancialPieData = (data) => {
    if (!data) return [];

    if (Array.isArray(data)) {
      return groupSum(
        data,
        (i) => i.label || i.name || "Unknown",
        (i) => i.value ?? i.amount ?? i.total ?? 0
      );
    }

    return [];
  };

  // -------------------------------
  // Build pie data per reportType
  // -------------------------------
  const buildPieData = ({ reportType, rawData, selectedResellerId }) => {
    switch (reportType) {
      case "user":
        return getUserPieData(rawData, selectedResellerId);
      case "usage":
        return getUsagePieData(rawData);
      case "commission":
        return getCommissionPieData(rawData, selectedResellerId);
      case "financial":
        return getFinancialPieData(rawData);
      default:
        return [];
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const isVeryNarrow = containerWidth < 400;
  const isNarrow = containerWidth < 700;
  const isWide = containerWidth >= 700;

  const chartData = useMemo(
    () =>
      buildPieData({
        reportType,
        rawData,
        selectedResellerId,
      }),
    [reportType, rawData, selectedResellerId]
  );

  const total = useMemo(
    () =>
      chartData.reduce(
        (sum, i) => sum + safeNumber(i.value),
        0
      ),
    [chartData]
  );

  // For `user` and `commission` we want to show the chart even if some values are 0.
  const hasAnyData =
    reportType === "usage" || reportType === "financial"
      ? total > 0
      : chartData.length > 0; // user / commission: show as long as items exist

  const margin = {
    top: isVeryNarrow ? 40 : 50,
    right: isWide ? 180 : 100,
    bottom: isVeryNarrow ? 120 : 100,
    left: 40,
  };

  const legendConfig = {
    anchor: "right",
    direction: "column",
    translateX: isWide ? 130 : 60,
    itemsSpacing: 6,
    itemWidth: 120,
    itemHeight: 20,
    symbolSize: 14,
  };

  const CenterLabel = ({ centerX, centerY }) => (
    <text
      x={centerX}
      y={centerY}
      textAnchor="middle"
      dominantBaseline="central"
      style={{
        fontSize: isVeryNarrow ? "1.5rem" : "2.2rem",
        fontWeight: 800,
        fill: fixedTextPrimary,
      }}
    >
      {total.toLocaleString()}
    </text>
  );

  if (defaultViewMode !== "pie-chart") return null;

  return (
    <div
      ref={chartContainerRef}
      style={{
        position: "relative",
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        padding: "16px",
        height: "540px",
      }}
    >
      {/* Dropdown: User + Commission show all resellers */}
      {(reportType === "user" || reportType === "commission") && (
        <div
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            zIndex: 10,
          }}
        >
          <select
            value={selectedResellerId}
            onChange={(e) => setSelectedResellerId(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontSize: "13px",
              background: "#fff",
            }}
          >
            <option value="">{displayText("All Resellers")}</option>
            {resellerOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: "center", marginTop: "200px" }}>
          Loading...
        </div>
      ) : !hasAnyData ? (
        <div style={{ textAlign: "center", marginTop: "200px" }}>
          No Data Found
        </div>
      ) : (
        <ResponsivePie
          // Keep ALL slices, even if value === 0 (for user / commission)
          data={chartData}
          margin={margin}
          innerRadius={0.5}
          padAngle={2.5}
          cornerRadius={6}
          activeOuterRadiusOffset={8}
          colors={{ datum: "data.color" }}
          borderWidth={2}
          borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
          sortByValue={true}
          enableArcLabels={true}
          arcLabelsSkipAngle={12}
          arcLabelsTextColor={fixedTextPrimary}
          enableArcLinkLabels={false}
          legends={[legendConfig]}
          layers={["arcs", "arcLabels", CenterLabel, "legends"]}
          tooltip={({ datum }) => {
  const d = datum.data;

  return (
    <div
      style={{
        background: tooltipBg,
        padding: "10px 14px",
        borderRadius: "10px",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        fontSize: "13px",
        lineHeight: "1.6",
        minWidth: "180px",
        maxWidth: "240px",
        whiteSpace: "normal",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          marginBottom: "6px",
          color: fixedTextPrimary,
          wordBreak: "break-word",
        }}
      >
        {d.label}
      </div>

      {/* User report */}
      {reportType === "user" && d.type === "reseller" && (
        <div>{displayText(" Total Client Admins: ")}<b>{d.totalClientAdmins}</b>
        </div>
      )}
      {reportType === "user" && d.type === "clientAdmin" && (
        <>
          <div>{clientUserText(" Total Users: ")}<b>{d.totalUsers}</b>
          </div>
          <div>{displayText(" Total Cases: ")}<b>{d.totalCases}</b>
          </div>
        </>
      )}

      {/* Commission report */}
      {reportType === "commission" && d.type === "reseller" && (
        <div>{displayText(" Total Cases: ")}<b>{d.value}</b>
          <br />
          Total Firms: <b>{d.totalFirms}</b>
        </div>
      )}
      {reportType === "commission" && d.type === "firm" && (
        <div>{displayText(" Total Cases: ")}<b>{d.totalCases}</b>
        </div>
      )}

      {/* Usage report */}
      {reportType === "usage" && (
        <div>
          Count: <b>{d.value}</b>
        </div>
      )}
    </div>
  );
}}
        />
      )}
    </div>
  );
};

export default ReportPieChart;